import {
    type Components,
    createTheme,
    type Theme as MuiTheme,
    type ThemeOptions,
    type ComponentsPropsList,
    useTheme as useMuiTheme,
} from '@mui/material/styles';
import {type Localization} from '@mui/material/locale';
import {type LinkProps} from '@mui/material/Link';
import formLabelClasses from '@mui/material/FormLabel/formLabelClasses';
import circularProgressClasses from '@mui/material/CircularProgress/circularProgressClasses';
import buttonClasses from '@mui/material/Button/buttonClasses';
import outlinedInputClasses from '@mui/material/OutlinedInput/outlinedInputClasses';
import inputAdornmentClasses from '@mui/material/InputAdornment/inputAdornmentClasses';
import buttonBaseClasses from '@mui/material/ButtonBase/buttonBaseClasses';
import autocompleteClasses from '@mui/material/Autocomplete/autocompleteClasses';
import avatarClasses from '@mui/material/Avatar/avatarClasses';
import {adjustAlpha} from '@famly/mf_colors';

import {SwitchThemeConfiguration} from './theme-configurations/switch-theme-configuration';
import {DatePickerThemeConfiguration} from './theme-configurations/date-picker-theme-configuration';
import {DateRangePickerThemeConfiguration} from './theme-configurations/date-range-picker-theme-configuration';
import {getEmphasizedFontWeight} from './get-emphasized-font-weight';

/**
 * MUI Theming
 */
const fontVariant = 'body';
export const LabelThemeConfiguration: Components<MuiTheme>['MuiFormLabel'] = {
    styleOverrides: {
        root: ({theme}) => ({
            fontWeight: getEmphasizedFontWeight(fontVariant),
            color: theme.modernFamlyTheme.colorPalette.n400,
            fontSize: theme.typography[fontVariant].fontSize,
            lineHeight: theme.typography[fontVariant].lineHeight,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',

            [`&.${formLabelClasses.disabled}`]: {
                color: theme.modernFamlyTheme.colorPalette.n200,

                [`.${formLabelClasses.asterisk}`]: {
                    color: theme.modernFamlyTheme.colorPalette.n200,
                },
            },

            [`.${formLabelClasses.asterisk}`]: {
                color: theme.modernFamlyTheme.colorPalette.r400,
            },
        }),
    },
};

export const FormHelperTextThemeConfiguration: Components<MuiTheme>['MuiFormHelperText'] = {
    styleOverrides: {
        root: ({theme}) => {
            return {
                ...theme.typography.micro,
                color: theme.modernFamlyTheme.colorPalette.n300,
                margin: 0,
            };
        },
        disabled: ({theme}) => {
            return {
                color: theme.modernFamlyTheme.colorPalette.n200,
            };
        },
    },
};

/**
 * Modern Famly theme
 */
export interface Theme {
    /**
     * The name of the theme
     */
    name: string;

    /**
     * The theme's color palette
     */
    colorPalette: ColorPalette;

    /**
     * Elevations
     */
    elevation: ElevationConfiguration;

    /**
     * Theme helper for spacing. Forwarded from Material.
     *
     * @see https://mui.com/material-ui/customization/spacing/
     */
    spacing: MuiTheme['spacing'];

    /**
     * Theme helper for breakpoints.
     *
     * @see https://mui.com/material-ui/customization/breakpoints/
     */
    breakpoints: MuiTheme['breakpoints'];

    /**
     * Theme helper for border radius.
     */
    shape: MuiTheme['shape'];
}

export const TypographyThemeConfiguration: Components<
    Omit<Theme, 'name' | 'colorPalette' | 'elevation'>
>['MuiTypography'] = {
    defaultProps: {
        variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            body: 'p',
            'body-small': 'p',
            micro: 'p',
        },
    },
};

export const DrawerThemeConfiguration: Components<MuiTheme>['MuiDrawer'] = {
    defaultProps: {
        anchor: 'bottom',
        open: false,
    },
};

type OwnerState = ComponentsPropsList['MuiChip'] & Record<string, unknown>;

const getPaddingLeft = (ownerState: OwnerState) => {
    switch (true) {
        case ownerState.size === 'compact' && Boolean(ownerState.avatar):
            return 0.5;
        case ownerState.size === 'compact' && Boolean(ownerState.icon):
        case ownerState.size === 'regular' && Boolean(ownerState.avatar):
        case ownerState.size === 'regular' && Boolean(ownerState.icon):
            return 2;
        default:
            return 3;
    }
};

const getPaddingRight = (ownerState: OwnerState) => {
    switch (true) {
        case ownerState.size === 'compact' && Boolean(ownerState.onDelete):
            return 1;
        case ownerState.size === 'regular' && Boolean(ownerState.onDelete):
            return 2;
        default:
            return 3;
    }
};

const AVATAR_GROUP_DEFAULT_MAX = 4;

export const AvatarGroupThemeConfiguration: Components<MuiTheme>['MuiAvatarGroup'] = {
    styleOverrides: {
        root: ({ownerState: {max = AVATAR_GROUP_DEFAULT_MAX}}) => ({
            // We want to stack the visible avatars from left to right, but MUI's default is
            // to stack from right to left. So we need to reverse the order of the visible items.
            // From: https://github.com/mui/material-ui/issues/30789#issuecomment-1054061085
            //
            // MUI also has this open issue suggesting to open this up to the user as a prop:
            // https://github.com/mui/material-ui/issues/30789
            ...[...Array(max)].reduce(
                (result, curr, index) => ({
                    ...result,
                    [`& > .${avatarClasses.root}:nth-of-type(${index + 1})`]: {
                        zIndex: max - index,
                    },
                }),
                {},
            ),
        }),
    },
};

export const ChipThemeConfiguration: Components<MuiTheme>['MuiChip'] = {
    defaultProps: {
        size: 'regular',
    },
    styleOverrides: {
        root: ({theme, ownerState}) => ({
            transitionDuration: '200ms',
            transitionProperty: 'color, background',
            borderRadius: '24px',
            background: theme.modernFamlyTheme.colorPalette.p100,
            color: theme.modernFamlyTheme.colorPalette.p500,
            height: ownerState.size === 'compact' ? '24px' : '36px',
            padding: theme.modernFamlyTheme.spacing(0),
            paddingRight: theme.modernFamlyTheme.spacing(getPaddingRight(ownerState)),
            paddingLeft: theme.modernFamlyTheme.spacing(getPaddingLeft(ownerState)),
            fontSize: theme.typography['body-small'].fontSize,
            lineHeight: theme.typography['body-small'].lineHeight,
            cursor: ownerState.disabled || !ownerState.onClick ? 'unset' : 'pointer',
            '&:hover': ownerState.onClick
                ? {
                      background: theme.modernFamlyTheme.colorPalette.p200,
                  }
                : undefined,
            '&.Mui-disabled': {
                opacity: '1',
                background: theme.modernFamlyTheme.colorPalette.n75,
                '& *': {
                    color: theme.modernFamlyTheme.colorPalette.n200,
                },
                '& .MuiAvatar-root': {
                    opacity: '0.3',
                },
            },
        }),
        label: ({ownerState}) => ({
            paddingLeft: Boolean(ownerState.icon) || Boolean(ownerState.avatar) ? '4px' : '0',
            paddingRight: ownerState.onDelete ? '4px' : 0,
        }),
    },
};

export const BadgeThemeConfiguration: Components<MuiTheme>['MuiBadge'] = {
    styleOverrides: {
        badge: ({theme}) => ({
            color: theme.modernFamlyTheme.colorPalette.n0,
            fontFamily: theme.typography.micro.fontFamily,
            fontSize: '10px',
            letterSpacing: '-2',
            lineHeight: '8px',
        }),
        dot: ({theme}) => ({
            width: '12px',
            height: '12px',
            borderRadius: theme.spacing(3),
        }),
    },
};

export const OutlinedInputThemeConfiguration: Components<MuiTheme>['MuiOutlinedInput'] = {
    defaultProps: {
        size: 'regular',
    },
    styleOverrides: {
        root: ({theme, ownerState}) => ({
            // In MUI's internal "OutlinedInput" component the border is not added directly
            // to the root element, because they support labels that "overlap" the border:
            //
            //       --- Label here --------------------------------------
            //       |                                                   |
            //       |  Value of input here                              |
            //       |                                                   |
            //       -----------------------------------------------------
            //
            // This is called the "notched input". However we don't support notched input in Modern Famly
            // and thus it's easier to apply the border directly to the root element and avoid unnecessary
            // deeply nested styling.
            [`& .${outlinedInputClasses.notchedOutline}`]: {
                display: 'none',
            },

            borderRadius: '8px',
            border: `1px solid ${theme.modernFamlyTheme.colorPalette.n200}`,
            backgroundColor: theme.modernFamlyTheme.colorPalette.n0,
            overflow: 'hidden',

            '&:hover': {
                borderColor: theme.modernFamlyTheme.colorPalette.p300,
            },
            '&.Mui-focused': {
                borderColor: theme.modernFamlyTheme.colorPalette.p400,
            },
            '&.Mui-error': {
                borderColor: theme.modernFamlyTheme.colorPalette.r400,
            },
            '&.Mui-disabled': {
                backgroundColor: theme.modernFamlyTheme.colorPalette.n75,
                borderColor: theme.modernFamlyTheme.colorPalette.n75,
            },

            // Following MUI's own styling scheme we add padding to the root element
            // when `multiline` is truthy. Padding will be added to the input element
            // when falsy.
            ...(ownerState.multiline && {
                padding:
                    ownerState.size === 'regular'
                        ? theme.modernFamlyTheme.spacing('13px', 3)
                        : theme.modernFamlyTheme.spacing(2, 3),
            }),
        }),
        input: ({theme, ownerState}) => ({
            color: theme.modernFamlyTheme.colorPalette.n400,
            height: 'initial',

            border: 'none !important',
            backgroundColor: 'inherit !important',

            fontSize: theme.typography[ownerState.size === 'compact' ? 'body-small' : 'body'].fontSize,
            lineHeight: theme.typography[ownerState.size === 'compact' ? 'body-small' : 'body'].lineHeight,

            '&.Mui-disabled': {
                WebkitTextFillColor: theme.modernFamlyTheme.colorPalette.n300,
            },

            '::placeholder': {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            // Following MUI's own styling scheme we add padding to the input element
            // when `multiline` is falsy. Padding will be added to the root element
            // when truthy.
            ...(!ownerState.multiline && {
                padding:
                    ownerState.size === 'regular'
                        ? theme.modernFamlyTheme.spacing('13px', 3)
                        : theme.modernFamlyTheme.spacing(2, 3),
            }),
        }),
        adornedStart: ({theme}) => ({
            paddingLeft: theme.modernFamlyTheme.spacing(2),

            [`&.Mui-disabled .${inputAdornmentClasses.positionStart}`]: {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            // This makes sure that the buttons used as adornment have the correct text color when the field is disabled.
            // This is the case for the NumericInput component which has button adornments
            [`&.Mui-disabled .${buttonBaseClasses.root}`]: {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            [`& .${inputAdornmentClasses.positionStart}`]: {
                marginRight: 0,
            },

            [`& .${outlinedInputClasses.input}`]: {
                paddingLeft: theme.modernFamlyTheme.spacing(1),
            },
        }),
        adornedEnd: ({theme}) => ({
            paddingRight: theme.modernFamlyTheme.spacing(2),

            [`&.Mui-disabled .${inputAdornmentClasses.positionEnd}`]: {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            // This makes sure that the buttons used as adornment have the correct text color when the field is disabled.
            // This is the case for the NumericInput component which has button adornments
            [`&.Mui-disabled .${buttonBaseClasses.root}`]: {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            [`& .${inputAdornmentClasses.positionEnd}`]: {
                marginLeft: 0,
            },

            [`& .${outlinedInputClasses.input}`]: {
                paddingRight: theme.modernFamlyTheme.spacing(1),
            },
        }),
    },
};

export const SelectThemeConfiguration: Components<MuiTheme>['MuiAutocomplete'] = {
    styleOverrides: {
        input: ({theme, ownerState}) => ({
            fontSize:
                ownerState.size === 'compact'
                    ? theme.typography['body-small'].fontSize
                    : theme.typography.body.fontSize,

            '&.MuiOutlinedInput-input': {
                padding: theme.modernFamlyTheme.spacing(1, 0),
            },
        }),
        inputRoot: ({theme, ownerState}) => ({
            minHeight: ownerState.size === 'compact' ? '36px' : '48px',
            paddingLeft: theme.modernFamlyTheme.spacing(3),
            paddingTop:
                ownerState.size === 'compact'
                    ? theme.modernFamlyTheme.spacing(1.25)
                    : theme.modernFamlyTheme.spacing(2.75),
            paddingBottom:
                ownerState.size === 'compact'
                    ? theme.modernFamlyTheme.spacing(1.25)
                    : theme.modernFamlyTheme.spacing(2.75),

            // When `multiple` is `true`, ensures that pills
            // shrink correctly inside the input field when
            // it's unfocused
            ...(ownerState.multiple === true
                ? {
                      '&:not(.Mui-focused)': {
                          flexWrap: ownerState.multiple === true ? 'nowrap' : 'wrap',

                          '& .MuiChip-root': {
                              flexShrink: '1',
                              flexGrow: '0',
                              minWidth: '0',
                          },
                      },
                  }
                : {}),

            '& .MuiInputAdornment-positionStart': {
                marginRight: theme.modernFamlyTheme.spacing(1),
            },

            [`&.Mui-disabled .${autocompleteClasses.endAdornment}`]: {
                color: theme.modernFamlyTheme.colorPalette.n300,
            },

            '&.Mui-disabled .MuiInputAdornment-positionStart': {
                opacity: 0.3,
            },
            '&.MuiInputBase-adornedStart': {
                gap: theme.modernFamlyTheme.spacing(1),
            },
            '.MuiAutocomplete-tag': {
                margin: 0,
                height: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                padding: theme.modernFamlyTheme.spacing(0, 2.5, 0, 2),
                fontSize: theme.typography['body-small'].fontSize,
                backgroundColor: theme.modernFamlyTheme.colorPalette.n100,
                borderRadius: '24px',
            },
            '.MuiOutlinedInput-input': {
                padding: '0 !important',

                // When `multiple` is `true`, don't show the input field
                // when one option or more options are selected. We can't use
                // `display: none` for this as the input field would never
                // recieve focus. Instead we shrink the width to 0.
                ...(ownerState.multiple === true
                    ? {
                          '&:not(:focus)': {
                              minWidth: (ownerState.value?.length ?? 0) > 0 ? '0px !important' : 'initial',
                              width: (ownerState.value?.length ?? 0) > 0 ? '0px !important' : 'initial',
                              flexGrow: (ownerState.value?.length ?? 0) > 0 ? '0 !important' : 'initial',
                          },
                      }
                    : {}),
            },
        }),
        listbox: () => ({
            padding: 0,
        }),
        // these styles will only be applied to the StandaloneMultiSelect component
        // we are applying custom styles for the groupLabel in the renderRow function
        groupLabel: ({theme}) => ({
            paddingLeft: 0,
            lineHeight: 'normal',
            marginTop: theme.modernFamlyTheme.spacing(2),
            paddingTop: theme.modernFamlyTheme.spacing(2),
            paddingBottom: theme.modernFamlyTheme.spacing(1),
            fontSize: theme.typography.body.fontSize,
        }),
    },
};

export const IconThemeConfiguration: Components<MuiTheme>['MuiIcon'] = {
    styleOverrides: {
        root: {
            fontFamily: 'Material Symbols Rounded',
            fontVariationSettings: `
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' 24
            `,
            fontWeight: 'normal',
            fontStyle: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            wordWrap: 'normal',
            lineHeight: '1em',
            direction: 'ltr',
            WebkitFontFeatureSettings: 'liga',
            WebkitFontSmoothing: 'antialiased',
            transition: 'font-variation-settings 0.1s ease-in-out',
        },
    },
};

export const ButtonThemeConfiguration: Components<MuiTheme>['MuiButton'] = {
    defaultProps: {
        variant: 'primary',
        size: 'regular',
    },
    styleOverrides: {
        // The styles here apply to all button instances
        root: ({theme, ownerState}) => {
            const palette = theme.modernFamlyTheme.colorPalette;
            const isTransparent = ownerState.variant === 'transparent' || ownerState.variant === 'critical-transparent';
            return {
                textTransform: 'none',
                boxSizing: 'border-box',
                borderRadius: '8px',
                fontWeight: '400',
                lineHeight: '22px',
                fontVariantLigatures: 'no-contextual',
                whiteSpace: 'nowrap',

                '&:disabled': {
                    backgroundColor: isTransparent ? 'transparent' : `${palette.n75}`,
                    borderColor: isTransparent ? 'transparent' : undefined,
                    color: `${adjustAlpha(0.6, palette.n300)}`,
                },

                [`.${buttonClasses.startIcon}`]: {
                    marginRight: theme.modernFamlyTheme.spacing(1),
                },

                [`.${circularProgressClasses.root}`]: {
                    color: theme.modernFamlyTheme.colorPalette.n300,
                },
            };
        },
    },
    variants: [
        /**
         * COLORS
         */
        {
            props: {variant: 'primary'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    backgroundColor: palette.p400,
                    color: palette.n0,

                    '&:hover': {
                        backgroundColor: palette.p300,
                    },

                    '&:active': {
                        backgroundColor: palette.p500,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.p400,
                    },
                };
            },
        },
        {
            props: {variant: 'secondary'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    backgroundColor: palette.p100,
                    color: palette.p500,

                    '&:hover,&:active': {
                        backgroundColor: palette.p200,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.p500,
                    },
                };
            },
        },
        {
            props: {variant: 'tertiary'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    border: `1px solid ${palette.n200}`,
                    backgroundColor: palette.n0,
                    color: palette.n400,

                    '&:hover,&:active': {
                        borderColor: palette.n200,
                        backgroundColor: palette.n75,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.n400,
                    },
                };
            },
        },
        {
            props: {variant: 'critical'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;
                return {
                    backgroundColor: palette.r200,
                    color: palette.r500,

                    '&:hover,&:active': {
                        backgroundColor: palette.r300,
                        color: palette.n0,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.r500,
                    },
                };
            },
        },
        {
            props: {variant: 'transparent'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    backgroundColor: 'transparent',
                    color: palette.n400,

                    '&:hover': {
                        backgroundColor: palette.n75,
                    },

                    '&:active': {
                        backgroundColor: palette.n100,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.n400,
                    },
                };
            },
        },
        {
            props: {variant: 'critical-transparent'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    backgroundColor: 'transparent',
                    color: palette.r400,

                    '&:hover': {
                        backgroundColor: palette.r300,
                        color: palette.n0,
                    },

                    '&:active': {
                        backgroundColor: palette.r400,
                        color: palette.n0,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.r400,
                    },
                };
            },
        },
        {
            props: {variant: 'upsell'},
            style: ({theme}) => {
                const palette = theme.modernFamlyTheme.colorPalette;

                return {
                    backgroundColor: palette.o400,
                    color: palette.n0,

                    '&:hover': {
                        backgroundColor: palette.o300,
                        color: palette.n0,
                    },

                    '&:active': {
                        backgroundColor: palette.o500,
                        color: palette.n0,
                    },

                    [`.${circularProgressClasses.root}`]: {
                        color: palette.o400,
                    },
                };
            },
        },

        /**
         * SIZES
         */
        {
            props: {size: 'regular'},
            style: ({theme}) => ({
                fontSize: theme.typography.body.fontSize,
                padding: theme.modernFamlyTheme.spacing(0, 4),
                height: '48px',
            }),
        },
        {
            props: {size: 'compact'},
            style: ({theme}) => ({
                fontSize: theme.typography['body-small'].fontSize,
                padding: theme.modernFamlyTheme.spacing(0, 3),
                height: '36px',
            }),
        },
    ],
};

/**
 * Converts a Modern Famly Theme to a theme that's compatible with MUI
 *
 * @param theme A Modern Famly theme
 * @returns A MUI compatible theme
 */
export const toMuiTheme = (
    theme: Theme,
    localization: Localization,
    config?: {LinkBehavior?: React.ElementType},
): MuiTheme => {
    return createTheme(
        {
            modernFamlyTheme: theme,
            breakpoints: theme.breakpoints,
            spacing: theme.spacing,
            shape: theme.shape,
            typography: TypographyThemeOptions,
            components: {
                MuiCssBaseline: {
                    styleOverrides: `
                    -webkit-font-smoothing: antialiased;
                `,
                },
                MuiButtonBase: {
                    defaultProps: {
                        LinkComponent: config?.LinkBehavior,
                    },
                },
                MuiLink: {
                    // The `as LinkProps` cast is necessary. This is what MUI does in their own documentation: https://mui.com/material-ui/guides/routing/#global-theme-link
                    defaultProps: {
                        component: config?.LinkBehavior,
                    } as LinkProps,
                },
                MuiButton: ButtonThemeConfiguration,
                MuiChip: ChipThemeConfiguration,
                MuiDrawer: DrawerThemeConfiguration,
                MuiFormLabel: LabelThemeConfiguration,
                MuiIcon: IconThemeConfiguration,
                MuiFormHelperText: FormHelperTextThemeConfiguration,
                MuiTypography: TypographyThemeConfiguration,
                MuiOutlinedInput: OutlinedInputThemeConfiguration,
                MuiAutocomplete: SelectThemeConfiguration,
                MuiUseMediaQuery: {
                    defaultProps: {
                        /**
                         * Tells MUI that we're not doing server-side rendering and as such
                         * don't need to cater for it when using the useMediaQuery hook.
                         *
                         * For more details, see https://mui.com/material-ui/react-use-media-query/#client-side-only-rendering
                         */
                        noSsr: true,
                    },
                },
                MuiAvatarGroup: AvatarGroupThemeConfiguration,
                MuiBadge: BadgeThemeConfiguration,
                MuiSwitch: SwitchThemeConfiguration,
                ...DatePickerThemeConfiguration,
                ...DateRangePickerThemeConfiguration,
            },
        },
        localization,
    );
};

/**
 * Get the current Modern Famly Theme
 *
 * @returns The current theme
 */
export const useTheme = (): Theme => {
    const muiTheme = useMuiTheme();

    return muiTheme.modernFamlyTheme;
};

/*
|------------------------------------------------------------------------------
| Module augmentation
|------------------------------------------------------------------------------
|
| Extends MUI's theme definition to include Modern Famly theme
|
*/
type ModernFamlyTheme = Theme;

declare module '@mui/material/styles' {
    interface Theme {
        modernFamlyTheme: ModernFamlyTheme;
    }

    interface ThemeOptions {
        modernFamlyTheme: ModernFamlyTheme;
    }

    interface BreakpointOverrides {
        xs: false;
        sm: false;
        md: false;
        lg: false;

        base: true;
        mobileLandscape: true;
        tabletPortrait: true;
        tabletLandscape: true;
        laptop: true;
        xl: true;
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        poster: React.CSSProperties;
        body: React.CSSProperties;
        'body-small': React.CSSProperties;
        micro: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        poster?: React.CSSProperties;
        body?: React.CSSProperties;
        'body-small'?: React.CSSProperties;
        micro?: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body: true;
        'body-small': true;
        micro: true;
        subtitle1: false;
        subtitle2: false;
        body1: false;
        body2: false;
        button: false;
        caption: false;
        overline: false;
        inherit: false;
    }
}

declare module '@mui/material/InputBase' {
    interface InputBasePropsSizeOverrides {
        medium: false;
        small: false;

        regular: true;
        compact: true;
    }
}

declare module '@mui/material/Autocomplete' {
    interface AutocompletePropsSizeOverrides {
        medium: false;
        small: false;

        regular: true;
        compact: true;
    }
}

declare module '@mui/material/Chip' {
    export interface ChipPropsSizeOverrides {
        medium: false;
        small: false;

        compact: true;
        regular: true;
    }
}

export const TypographyThemeOptions: ThemeOptions['typography'] = {
    fontFamily: 'Matter, "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
    h1: {
        fontSize: '64px',
        fontWeight: 400,
        lineHeight: '72px',
        letterSpacing: '-0.01em',
    },
    h2: {
        fontSize: '48px',
        fontWeight: 400,
        lineHeight: '56px',
        letterSpacing: '-0.01em',
    },
    h3: {
        fontSize: '40px',
        fontWeight: 400,
        lineHeight: '48px',
        letterSpacing: '-0.01em',
    },
    h4: {
        fontSize: '32px',
        fontWeight: 400,
        lineHeight: '40px',
        letterSpacing: '-0.01em',
    },
    h5: {
        fontSize: '24px',
        fontWeight: 400,
        lineHeight: '32px',
        letterSpacing: '-0.01em',
    },
    h6: {
        fontSize: '20px',
        fontWeight: 400,
        lineHeight: '28px',
        letterSpacing: '-0.01em',
    },
    body: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '20px',
    },
    'body-small': {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '18px',
    },
    micro: {
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
        letterSpacing: '-0.02em',
    },
};

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        outlined: false;
        contained: false;
        text: false;

        primary: true;
        secondary: true;
        tertiary: true;
        transparent: true;
        critical: true;
        'critical-transparent': true;
        upsell: true;
    }

    export interface ButtonPropsSizeOverrides {
        small: false;
        medium: false;
        large: false;

        regular: true;
        compact: true;
    }
}
